/**
 * Customer 360 hero panel.
 *
 * The first thing every staff conversation orients on:
 *   - avatar (initials in brand OKLCH)
 *   - name, email, phone, address (inline-editable via "Edit profile" action)
 *   - tags (chip row)
 *   - lifecycle stage badge (derived)
 *   - LTV (lifetime spend), last visit (relative), predicted churn risk
 *   - quick actions: New booking, Send message, Add note, Add credit
 */
import type { Customer360Response } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import {
  AlertTriangle,
  CalendarPlus,
  Coins,
  Crown,
  FileSignature,
  Gift,
  MessageSquare,
  Pencil,
  Snowflake,
  Sparkles,
  StickyNote,
  UserRound,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';

type Customer = Customer360Response['customer'];
type LifecycleStage = Customer360Response['lifecycleStage'];

interface Props {
  data: Customer360Response;
  currency: string;
  onEdit: () => void;
  onIssueCredit: () => void;
  onAddNote: () => void;
  /** Waiver sign-by-link: opens the "Send waiver link" drawer. Hidden when omitted. */
  onSendWaiverLink?: () => void;
  onComputeChurn: () => void;
  canViewChurn: boolean;
  computingChurn: boolean;
}

const STAGE_CONFIG: Record<
  LifecycleStage,
  { label: string; className: string; icon: typeof Crown }
> = {
  vip: {
    label: 'VIP',
    className: 'bg-amber-100 text-amber-900 border-amber-300/60',
    icon: Crown,
  },
  regular: {
    label: 'Regular',
    className: 'bg-emerald-100 text-emerald-900 border-emerald-300/60',
    icon: Sparkles,
  },
  new: {
    label: 'New',
    className: 'bg-sky-100 text-sky-900 border-sky-300/60',
    icon: Zap,
  },
  lapsed: {
    label: 'Lapsed',
    className: 'bg-orange-100 text-orange-900 border-orange-300/60',
    icon: Snowflake,
  },
  churned: {
    label: 'Churned',
    className: 'bg-rose-100 text-rose-900 border-rose-300/60',
    icon: AlertTriangle,
  },
};

/** Quick-action buttons: 44px targets below `md`, compact on desktop. */
const ACTION_BASE =
  'inline-flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-sm font-medium press md:min-h-0 md:px-3';
const PRIMARY_ACTION = `${ACTION_BASE} bg-primary text-primary-foreground hover:bg-primary/90`;
const SECONDARY_ACTION = `${ACTION_BASE} border border-border bg-surface hover:bg-secondary`;

function initialsOf(c: Customer): string {
  const f = (c.firstName || '').trim();
  const l = (c.lastName || '').trim();
  return ((f[0] ?? '') + (l[0] ?? '')).toUpperCase() || '?';
}

function relativeDays(iso: string | null): string {
  if (!iso) return 'No visits yet';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days < 1) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)} yr ago`;
}

export function Customer360Hero({
  data,
  currency,
  onEdit,
  onIssueCredit,
  onAddNote,
  onSendWaiverLink,
  onComputeChurn,
  canViewChurn,
  computingChurn,
}: Props) {
  const c = data.customer;
  const stage = STAGE_CONFIG[data.lifecycleStage];
  const StageIcon = stage.icon;
  const address = [c.addressLine1, c.city, c.postcode].filter(Boolean).join(', ');

  // Pick out the latest checked-out booking's checkout time as "last visit" surrogate
  // (the API exposes stats.completedBookings + the actual visit time on the booking row).
  const lastVisitIso = (() => {
    for (const entry of data.bookings) {
      const s = entry.booking?.status as string | undefined;
      if (s === 'checked_out' || s === 'completed') {
        const co =
          (entry.stay as { checkout?: string } | null)?.checkout ??
          (entry.booking as { updatedAt?: string }).updatedAt;
        return co ?? null;
      }
    }
    return null;
  })();

  return (
    <section
      className="rounded-2xl border border-border bg-surface px-5 py-5 md:px-7 md:py-6"
      data-testid="customer360-hero"
    >
      <div className="flex flex-col gap-5 md:flex-row md:items-start">
        {/* Avatar */}
        <div className="shrink-0 flex md:block items-center gap-4">
          <div
            className="grid h-20 w-20 place-items-center rounded-full text-2xl font-semibold text-white shadow-sm md:h-24 md:w-24 md:text-3xl"
            style={{ background: 'oklch(0.55 0.18 248)' }}
            aria-hidden
          >
            {initialsOf(c as Customer)}
          </div>
          <div className="md:hidden">
            <h1 className="text-xl font-semibold tracking-tight">
              {String(c.firstName ?? '')} {String(c.lastName ?? '')}
            </h1>
            <span
              className={[
                'mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium',
                stage.className,
              ].join(' ')}
            >
              <StageIcon className="size-3" strokeWidth={1.8} />
              {stage.label}
            </span>
          </div>
        </div>

        {/* Identity */}
        <div className="flex-1 min-w-0">
          <div className="hidden md:flex md:items-center md:gap-3">
            <h1 className="truncate text-2xl font-semibold tracking-tight">
              {String(c.firstName ?? '')} {String(c.lastName ?? '')}
            </h1>
            <span
              className={[
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                stage.className,
              ].join(' ')}
            >
              <StageIcon className="size-3" strokeWidth={1.8} />
              {stage.label}
            </span>
          </div>

          <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
            {typeof c.email === 'string' && c.email && (
              <div className="flex items-center gap-2">
                <dt className="sr-only">Email</dt>
                <dd className="num truncate text-muted-foreground">{c.email}</dd>
              </div>
            )}
            {typeof c.phone === 'string' && c.phone && (
              <div className="flex items-center gap-2">
                <dt className="sr-only">Phone</dt>
                <dd className="num truncate text-muted-foreground">{c.phone}</dd>
              </div>
            )}
            {address && (
              <div className="sm:col-span-2 flex items-center gap-2">
                <dt className="sr-only">Address</dt>
                <dd className="truncate text-muted-foreground">{address}</dd>
              </div>
            )}
          </dl>

          {/* Tags */}
          {data.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {data.tags.map((t, i) => {
                const tag = (t as { tag?: { id?: string; label?: string; color?: string } }).tag;
                if (!tag) return null;
                return (
                  <Link
                    key={tag.id ?? i}
                    to={`/customers?tag=${tag.id ?? ''}`}
                    className="inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-medium text-foreground hover:bg-secondary/80 press"
                    style={tag.color ? { background: tag.color } : undefined}
                  >
                    {tag.label ?? ''}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Key metrics */}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Metric
              label="Lifetime spend"
              value={formatPriceMinor(data.paymentsTotalsMinor.net, currency)}
            />
            <Metric label="Last visit" value={relativeDays(lastVisitIso)} />
            <Metric
              label="Churn risk"
              value={
                data.churnScore ? `${data.churnScore.score}/100` : canViewChurn ? '—' : 'Hidden'
              }
              hint={data.churnScore?.suggestedAction ?? undefined}
              tone={
                data.churnScore
                  ? data.churnScore.score >= 70
                    ? 'danger'
                    : data.churnScore.score >= 40
                      ? 'warn'
                      : 'ok'
                  : 'muted'
              }
              action={
                canViewChurn ? (
                  <button
                    type="button"
                    onClick={onComputeChurn}
                    disabled={computingChurn}
                    className="text-[10.5px] font-medium uppercase tracking-wide text-primary hover:underline press disabled:opacity-50"
                  >
                    {computingChurn ? 'Scoring…' : data.churnScore ? 'Refresh' : 'Compute'}
                  </button>
                ) : null
              }
            />
          </div>
        </div>

        {/* Quick actions — phones: full-width primary + 2-up grid of compact
            44px buttons; desktop: the original stacked column. */}
        <div
          className="grid grid-cols-2 gap-2 md:flex md:w-44 md:shrink-0 md:flex-col"
          data-testid="customer360-actions"
        >
          <Link to={`/bookings/new?customerId=${c.id}`} className={`${PRIMARY_ACTION} col-span-2`}>
            <CalendarPlus className="size-4 shrink-0" strokeWidth={1.75} />
            New booking
          </Link>
          <button type="button" onClick={onEdit} className={SECONDARY_ACTION}>
            <Pencil className="size-4 shrink-0" strokeWidth={1.75} />
            Edit profile
          </button>
          <button type="button" onClick={onAddNote} className={SECONDARY_ACTION}>
            <StickyNote className="size-4 shrink-0" strokeWidth={1.75} />
            Add note
          </button>
          <button type="button" onClick={onIssueCredit} className={SECONDARY_ACTION}>
            <Coins className="size-4 shrink-0" strokeWidth={1.75} />
            Add credit
          </button>
          {onSendWaiverLink && (
            <button type="button" onClick={onSendWaiverLink} className={SECONDARY_ACTION}>
              <FileSignature className="size-4 shrink-0" strokeWidth={1.75} />
              <span className="truncate">Send waiver link</span>
            </button>
          )}
        </div>
      </div>

      {data.churnScore && data.churnScore.signals.length > 0 && (
        <div className="mt-4 rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm">
          <p className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            <UserRound className="size-3" strokeWidth={1.75} />
            Churn signals
          </p>
          <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
            {data.churnScore.signals.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
          {data.churnScore.suggestedAction && (
            <p className="mt-2 flex items-start gap-1.5 text-sm">
              <Gift className="mt-0.5 size-3.5 text-primary" strokeWidth={1.75} />
              <span>{data.churnScore.suggestedAction}</span>
            </p>
          )}
        </div>
      )}

      <button type="button" className="sr-only" onClick={() => undefined}>
        <MessageSquare className="size-4" />
      </button>
    </section>
  );
}

interface MetricProps {
  label: string;
  value: string;
  hint?: string | undefined;
  tone?: 'ok' | 'warn' | 'danger' | 'muted' | undefined;
  action?: React.ReactNode | undefined;
}

function Metric({ label, value, tone = 'muted', hint, action }: MetricProps) {
  const toneClass =
    tone === 'danger'
      ? 'text-rose-700'
      : tone === 'warn'
        ? 'text-amber-700'
        : tone === 'ok'
          ? 'text-emerald-700'
          : 'text-foreground';
  return (
    <div className="rounded-xl border border-border bg-background/60 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </p>
        {action}
      </div>
      <p className={['num mt-0.5 text-base font-semibold tracking-tight', toneClass].join(' ')}>
        {value}
      </p>
      {hint && <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
